import React, { useRef, useState, useEffect, useCallback } from "react";
import { getUserTimezone } from "src/pages/providerPayRequestDashboard/utils/dateUtils";
import { convertTimeZone } from "src/lib/date-time.util";
import "./StepProgressBar.scss";

/**
 * StepProgressBar Component
 * Displays a horizontal progress bar with steps showing completion status
 */
const StepProgressBar = ({ steps = [], localTimezone = null }) => {
  const scrollContainerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScrollability = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
  }, []);

  useEffect(() => {
    checkScrollability();
    window.addEventListener("resize", checkScrollability);
    return () => window.removeEventListener("resize", checkScrollability);
  }, [checkScrollability, steps]);

  const handleScroll = (direction) => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const scrollAmount = 100;
    container.scrollBy({
      left: direction === "left" ? -scrollAmount : scrollAmount,
      behavior: "smooth",
    });
  };

  const formatDate = (dateString, useLocalTimezone = false) => {
    if (!dateString) return "";
    return (
      convertTimeZone({
        date: dateString,
        convertTimeZone: (useLocalTimezone && localTimezone) || getUserTimezone(),
        outputFormat: "MM/dd/yyyy",
      }) || dateString.split(" ")[0]
    );
  };

  const showScrollButtons = steps.length > 3;

  return (
    <div className="step-progress-bar-container">
      <div className="step-progress-bar-outer">
        <div
          ref={scrollContainerRef}
          className={`step-progress-bar-container ${steps.length <= 3 ? "step-progress-bar-container-flex" : ""}`}
          onScroll={checkScrollability}
        >
          <div className={`step-progress-bar-wrapper ${steps.length <= 3 ? "step-progress-bar-wrapper-flex" : ""}`}>
          {steps.map((step, index) => {
            const isLast = index === steps.length - 1;
            const isCompleted = step.status === "completed";
            const isCurrent = step.status === "current";
            const isRejected = step.status === "rejected";

            return (
              <div key={index} className={`step-progress-item ${steps.length <= 3 ? "step-progress-item-flex" : ""}`}>
                {/* Connecting Line */}
                {!isLast && <div className={`step-line ${isCompleted ? "step-line-completed" : "step-line-pending"}`} />}

                {/* Circle */}
                <div
                  className={`step-circle ${
                    isCompleted
                      ? "step-circle-completed"
                      : isCurrent
                        ? "step-circle-current"
                        : isRejected
                          ? "step-circle-rejected"
                          : "step-circle-pending"
                  }`}
                >
                  {isCompleted && <span className="k-icon k-i-check step-circle-icon" style={{ color: "white" }}></span>}
                  {isRejected && <span className="k-icon k-i-close step-circle-icon" style={{ color: "#dc3545" }}></span>}
                  {isCurrent && <div className="step-circle-inner-dot"></div>}
                </div>

                {/* Label */}
                <div className={`step-label ${isCompleted || isCurrent ? "step-label-active" : "step-label-inactive"}`}>
                  {step.label}
                </div>

                {/* Approver Name */}
                {step.name && <div className="step-sublabel">{step.name}</div>}

                {/* Date: first step is the provider's submission (provider-local); later steps are reviewer actions (viewer-local) */}
                {step.status_date && <div className="step-sublabel">{formatDate(step.status_date, index === 0)}</div>}
              </div>
            );
          })}
          </div>
        </div>
      </div>
      <div className="step-progress-bar-scroll-buttons">
        {showScrollButtons && (
          <button
            className="step-scroll-btn step-scroll-btn-left"
            onClick={() => handleScroll("left")}
            aria-label="Scroll left"
          >
            <span className="k-icon k-i-arrow-chevron-left"></span>
          </button>
        )}
        {showScrollButtons && (
          <button
            className="step-scroll-btn step-scroll-btn-right"
            onClick={() => handleScroll("right")}
            aria-label="Scroll right"
          >
            <span className="k-icon k-i-arrow-chevron-right"></span>
          </button>
        )}
      </div>
    </div>
  );
};

export default StepProgressBar;

